import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Settings as SettingsIcon, Mail, BellRing, Zap, Gift, Copy, Users, Volume2, Trophy, KeyRound, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { SOUND_OPTIONS, getAlertSound, setAlertSound, playAlertSound } from "@/lib/alertSounds";

const PREFS = [
  {
    key: "alert_emails",
    icon: Zap,
    title: "Instant price alert emails",
    desc: "The moment one of your price alerts fires, get an email with the trigger details",
  },
  {
    key: "health_digest",
    icon: Mail,
    title: "Weekly Portfolio Health email",
    desc: "Every Monday — conflicts, exit risks & take-profit calls for your real brokerage holdings",
  },
  {
    key: "mute_digest",
    icon: BellRing,
    title: "Weekly Mute Report email",
    desc: "Every Monday — a recap of the signals you muted and what they did after",
  },
  {
    key: "winners_email",
    icon: Trophy,
    title: "Weekly Winners email",
    desc: "Every Monday — the leaderboard's top streaks delivered to your inbox as motivation",
  },
  {
    key: "login_alerts",
    icon: ShieldAlert,
    title: "New device sign-in alerts",
    desc: "Get an email whenever your account signs in from a device we haven't seen before",
  },
];

function ChangePasswordCard({ authHeaders }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeaders() }
      );
      toast.success("Password updated — other devices were signed out");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update password");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#00E0FF]/50";

  return (
    <Card className="glass-effect border border-white/10 mt-6" data-testid="change-password-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-[#00E0FF]" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Change Password</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">For email/password accounts. Google accounts don't have a password.</p>
        <form onSubmit={handleSubmit} className="space-y-3 max-w-md">
          <Input data-testid="current-password-input" type="password" placeholder="Current password"
            value={current} onChange={(e) => setCurrent(e.target.value)} required className={inputCls} />
          <Input data-testid="new-password-input" type="password" placeholder="New password (min 8 characters)"
            value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} className={inputCls} />
          <Input data-testid="confirm-password-input" type="password" placeholder="Confirm new password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className={inputCls} />
          <Button data-testid="change-password-submit" type="submit" disabled={saving}
            className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Update Password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function AlertSoundCard() {
  const [sound, setSound] = useState(getAlertSound());

  const onChange = (value) => {
    setSound(value);
    setAlertSound(value);
    playAlertSound(value);
    toast.success(value === "none" ? "Alert sound turned off" : "Alert sound updated");
  };

  return (
    <Card className="glass-effect border border-white/10 mt-6" data-testid="alert-sound-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Volume2 className="w-4 h-4 text-[#00E0FF]" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Alert sound</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Chime played in-app when a new trading signal alert fires (saved on this device)
        </p>
        <Select value={sound} onValueChange={onChange}>
          <SelectTrigger data-testid="alert-sound-select" className="w-full sm:w-64 bg-black/20 border-white/10 text-white">
            <SelectValue placeholder="Choose a sound" />
          </SelectTrigger>
          <SelectContent>
            {SOUND_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`alert-sound-${o.value}`}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardContent>
    </Card>
  );
}

function ReferralCard({ authHeaders }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/referrals/me`, { headers: authHeaders() })
      .then(({ data }) => setInfo(data))
      .catch((err) => console.debug("Referral info unavailable:", err));
  }, [authHeaders]);

  if (!info) return null;
  const link = `${window.location.origin}/?ref=${info.code}`;

  return (
    <Card className="glass-effect border border-[#00FFA3]/15 mt-6" data-testid="referral-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Gift className="w-4 h-4 text-[#00FFA3]" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Referral rewards</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Earn <span className="text-[#00FFA3] font-bold">+{info.bonus_per_referral} prediction credits</span> every time a friend signs up through your link
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="flex-1 min-w-[220px] truncate rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs text-[#00E0FF] mono" data-testid="referral-link">
            {link}
          </code>
          <Button
            data-testid="referral-copy-btn"
            size="sm"
            className="bg-[#00FFA3] text-black hover:bg-[#00FFA3]/90 font-bold h-9"
            onClick={() => {
              navigator.clipboard.writeText(link);
              toast.success("Referral link copied!");
            }}
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
          </Button>
        </div>
        <div className="flex items-center gap-2 mt-4 text-xs text-gray-400" data-testid="referral-stats">
          <Users className="w-3.5 h-3.5 text-gray-500" />
          {info.referred_count} friend{info.referred_count === 1 ? "" : "s"} joined · {info.credits_earned} credits earned
        </div>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { isAuthenticated, authHeaders } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(null);

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/preferences/emails`, { headers: authHeaders() });
      setPrefs(data);
    } catch {
      toast.error("Could not load preferences");
    }
  }, [authHeaders]);

  useEffect(() => {
    if (isAuthenticated) fetchPrefs();
  }, [isAuthenticated, fetchPrefs]);

  const toggle = async (key, value) => {
    setSaving(key);
    setPrefs((p) => ({ ...p, [key]: value }));
    try {
      const { data } = await axios.put(`${API}/preferences/emails`, { [key]: value }, { headers: authHeaders() });
      setPrefs(data);
      toast.success(value ? "Email enabled" : "Email turned off");
    } catch {
      setPrefs((p) => ({ ...p, [key]: !value }));
      toast.error("Could not save preference");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] relative">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />

      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            data-testid="settings-back-btn"
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <SettingsIcon className="w-10 h-10 text-[#00E0FF]" />
            <h1 className="text-4xl font-bold tracking-tight text-white" style={{ fontFamily: "Unbounded" }}>
              SETTINGS
            </h1>
          </div>
          <p className="text-gray-400">Control which emails land in your inbox</p>
        </motion.div>

        {!isAuthenticated ? (
          <Card className="glass-effect border border-white/10">
            <CardContent className="p-8 text-center" data-testid="settings-signin-prompt">
              <p className="text-gray-400">Sign in from the dashboard to manage your email preferences.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="glass-effect border border-white/10">
            <CardContent className="p-6">
              <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Email digests</h2>
              <div className="space-y-1" data-testid="email-prefs-list">
                {PREFS.map(({ key, icon: Icon, title, desc }) => (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-4 rounded-lg px-3 py-4 hover:bg-white/[0.03] transition-colors"
                    data-testid={`pref-row-${key}`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <Icon className="w-4 h-4 text-[#00E0FF] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white">{title}</div>
                        <div className="text-xs text-gray-500">{desc}</div>
                      </div>
                    </div>
                    <Switch
                      data-testid={`pref-toggle-${key}`}
                      checked={prefs ? !!prefs[key] : false}
                      disabled={!prefs || saving === key}
                      onCheckedChange={(v) => toggle(key, v)}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-600 mt-4">
                Emails are sent every Monday morning (UTC). Turning a digest off takes effect immediately.
              </p>
            </CardContent>
            </Card>
            <AlertSoundCard />
            <ChangePasswordCard authHeaders={authHeaders} />
            <ReferralCard authHeaders={authHeaders} />
          </>
        )}
      </main>
    </div>
  );
}
